import { Box, Chip, Divider, Grid, Typography } from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentIcon from "@mui/icons-material/Payment";
import { currencyRound } from "../../util/DoubleUtil";
import { dateTimeFormat } from "../../util/DateUtil";

const InfoRow = ({ label, children }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ textAlign: "right" }}>
      {children ?? "—"}
    </Typography>
  </Box>
);

const SectionCard = ({ icon, title, children }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      p: 2,
      bgcolor: "background.paper",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      {icon}
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
      >
        {title}
      </Typography>
    </Box>
    <Divider sx={{ mb: 1.5 }} />
    {children}
  </Box>
);

const PixTransacaoViewV2 = ({ pixTransacao }) => {
  const quitado = pixTransacao?.quitado;

  return (
    <Grid container spacing={2}>
      {/* Header */}
      <Grid size={12}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <QrCode2Icon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Pix transação
          </Typography>
          <Chip
            label={quitado ? "Quitado" : "Pendente"}
            color={quitado ? "success" : "warning"}
            size="small"
            sx={{ ml: "auto" }}
          />
        </Box>
      </Grid>

      {/* Transação */}
      <Grid size={6}>
        <SectionCard
          icon={<ReceiptIcon fontSize="small" color="action" />}
          title="Transação"
        >
          <InfoRow label="Código">{pixTransacao?.codigo}</InfoRow>
          <InfoRow label="Tipo">
            {pixTransacao?.tipo && (
              <Chip
                label={pixTransacao.tipo}
                size="small"
                variant="outlined"
                color="info"
              />
            )}
          </InfoRow>
          <InfoRow label="Valor">
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {currencyRound(pixTransacao?.valor)}
            </Typography>
          </InfoRow>
          <InfoRow label="Segundos para expiração">
            {pixTransacao?.segundosExpiracao}
          </InfoRow>
          <InfoRow label="Identificador">
            <Typography
              variant="body2"
              fontFamily="monospace"
              fontSize={12}
              color="text.secondary"
            >
              {pixTransacao?.identificador}
            </Typography>
          </InfoRow>
          <InfoRow label="Retorno">
            {pixTransacao?.retorno && (
              <Chip label={pixTransacao.retorno} size="small" color="success" />
            )}
          </InfoRow>
          {pixTransacao?.detalhesErro && (
            <InfoRow label="Detalhes do erro">
              <Typography variant="body2" color="error">
                {pixTransacao.detalhesErro}
              </Typography>
            </InfoRow>
          )}
        </SectionCard>
      </Grid>

      {/* Cliente */}
      <Grid size={6}>
        <SectionCard
          icon={<PersonIcon fontSize="small" color="action" />}
          title="Cliente"
        >
          <InfoRow label="Nome">{pixTransacao?.clienteNome}</InfoRow>
          <InfoRow label="CPF / CNPJ">
            <Typography variant="body2" fontFamily="monospace">
              {pixTransacao?.clienteCPFCNPJ}
            </Typography>
          </InfoRow>
          <InfoRow label="Descrição">{pixTransacao?.clienteDescricao}</InfoRow>
        </SectionCard>
      </Grid>

      {/* QR Code + Pagamento lado a lado */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <SectionCard
          icon={<QrCode2Icon fontSize="small" color="action" />}
          title="QR Code"
        >
          {pixTransacao?.qrCodeImagem ? (
            <Box
              component="img"
              src={pixTransacao.qrCodeImagem}
              alt="QR Code Pix"
              sx={{
                width: 120,
                height: 120,
                borderRadius: 1,
                display: "block",
                mb: 1,
              }}
            />
          ) : (
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <QrCode2Icon sx={{ fontSize: 48, color: "text.disabled" }} />
            </Box>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
          >
            {pixTransacao?.qrCodeTexto}
          </Typography>
        </SectionCard>
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <SectionCard
          icon={<PaymentIcon fontSize="small" color="action" />}
          title="Pagamento"
        >
          <InfoRow label="Data">
            {dateTimeFormat(pixTransacao?.dataPagamento)}
          </InfoRow>
          <InfoRow label="Valor pago">
            <Typography variant="body2" fontWeight={700}>
              {currencyRound(pixTransacao?.valorPago)}
            </Typography>
          </InfoRow>
          <InfoRow label="Quitado">
            <Chip
              label={quitado ? "Sim" : "Não"}
              color={quitado ? "success" : "default"}
              size="small"
            />
          </InfoRow>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

export default PixTransacaoViewV2;
